'use client';

import { useTheme } from 'next-themes';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { appHasMounted, markAppMounted } from '@/hooks/appMounted';
import { theme } from '@/lib/ui/theme';

interface OriginSectionProps {
  isLoading?: boolean;
}

export function OriginSection({ isLoading = false }: OriginSectionProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(appHasMounted());

  useEffect(() => {
    markAppMounted();
    setMounted(true);
  }, []);

  const isDark = mounted && resolvedTheme === 'dark';

  if (isLoading || !mounted) {
    return (
      <section className="px-6 py-12 md:px-16 md:py-16">
        <div className="mx-auto max-w-[1200px]">
          <Skeleton className="mx-auto mb-8 h-10 w-[50%]" />
          <Skeleton className="mx-auto h-48 w-full max-w-[700px] rounded-xl" />
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 py-12 md:px-16 md:py-16">
      <div className="mx-auto max-w-[1200px]">
        <h2
          className="text-gradient-hero mb-10 text-center text-3xl font-bold tracking-tight md:text-4xl"
          style={{ fontFamily: theme.fonts.display }}
        >
          Built from failure, not theory.
        </h2>

        <div
          className="mx-auto max-w-[700px] py-2 pl-6 md:pl-8"
          style={{
            borderLeft: `4px solid ${theme.box.purpleNumber.color[isDark ? 'dark' : 'light']}`,
          }}
        >
          <p
            className="mb-6 text-xl font-medium md:text-2xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            I documented more than 6,000 failures from AI agents writing real
            code: broken architecture, duplicated logic, performance and
            security holes, and the same mistakes on repeat.
          </p>

          <p
            className="text-foreground/80 mb-6 text-base md:text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            The same failure patterns kept showing up behind confident claims
            that the work was complete.
          </p>

          <p
            className="mb-6 text-base md:text-lg"
            style={{ fontFamily: theme.fonts.body }}
          >
            The solution wasn't better prompts or agent rules.
          </p>

          <p
            className="mb-8 text-xl font-semibold md:text-2xl"
            style={{ fontFamily: theme.fonts.display }}
          >
            The solution was enforcement.
          </p>

          <p
            className="text-foreground/70 text-base italic"
            style={{ fontFamily: theme.fonts.body }}
          >
            Bryce, Founder
          </p>
        </div>
      </div>
    </section>
  );
}
